import { SurveyAnswers } from "@core/types";
import i18n from "../../i18n";

const REQUIRED_ERROR_MESSAGES = {
  PHONE: i18n.t("Please enter phone number"),
  FULL_NAME: i18n.t("Please enter full name"),
  CITY: i18n.t("Please enter city"),
  DELIVERY_ADDRESS: i18n.t("Please enter delivery address"),
  POSITION: i18n.t("Please enter position"),
  COMPANY_NAME: i18n.t("Please enter company name"),
  PURCHASE_RESPONSIBLE: i18n.t("Please enter purchase responsible"),
  ESTABLISHMENT_TYPE: i18n.t("Please select establishment type"),
  ESTABLISHMENT_NAME: i18n.t("Please enter establishment name"),
  ESTABLISHMENT_COUNT: i18n.t("Please enter establishment count"),
  ESTABLISHMENT_CHARACTERISTICS: i18n.t("Please select establishment characteristics"),
  MULTIPLE_ESTABLISHMENTS: i18n.t("Please enter multiple establishments"),
  STORE_NAME: i18n.t("Please enter store name"),
} as const;

export type FieldType = "text" | "tel" | "radio" | "checkbox" | "number";
export type FieldName = keyof SurveyAnswers;

export type FieldOption = {
  name: FieldName;
  type: FieldType;
  required: boolean | string;
  label: string;
  hint?: string;
  options?: string[];
  hasCustomOption?: boolean;
  customOptionLabel?: "Свой вариант" | string;
  condition?: {
    field: FieldName;
    value: string;
  };
};

export type ChoiceConfig = {
  id: string;
  choice: string;
  fields: FieldOption[];
  allowSelectTheSameTime: boolean;
};

export const choicesConfig: ChoiceConfig[] = [
  {
    id: "1",
    allowSelectTheSameTime: false,
    choice: "Я готовлю дома, как частное лицо или вариант - Я готовлю для себя и семьи",
    fields: [
      { name: "phoneNumber", type: "tel", required: REQUIRED_ERROR_MESSAGES.PHONE, label: "Номер телефона" },
      { name: "fullName", type: "text", required: false, label: "Ф.И.О." },
      {
        name: "city",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.CITY,
        label: "Населенный пункт",
        hint: "район, город, село и т.д.",
      },
      {
        name: "deliveryAddress",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.DELIVERY_ADDRESS,
        label: "Адрес доставки",
      },
    ],
  },
  {
    id: "2",
    allowSelectTheSameTime: false,
    choice: "Я готовлю дома, для реализации  через социальные сети и т.д.",
    fields: [
      { name: "phoneNumber", type: "tel", required: REQUIRED_ERROR_MESSAGES.PHONE, label: "Номер телефона" },
      {
        name: "fullName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.FULL_NAME,
        label: "Ф.И.О.",
        hint: "Нам важно знать Ваше имя чтоб внести Вас как нашего клиента (Отчество не обязательно)",
      },
      { name: "city", type: "text", required: REQUIRED_ERROR_MESSAGES.CITY, label: "Населенный пункт" },
      {
        name: "deliveryAddress",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.DELIVERY_ADDRESS,
        label: "Адрес доставки",
      },
      { name: "brandName", type: "text", required: false, label: "Название Вашего бренда (если он есть)" },
    ],
  },
  {
    id: "3",
    allowSelectTheSameTime: true,
    choice: "У нас/у меня производство кондитерских изделий для реализаций в общепите или в магазинах",
    fields: [
      {
        name: "companyName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.COMPANY_NAME,
        label: "Укажите название производства или бренда",
      },
      {
        name: "purchaseResponsible",
        type: "radio",
        required: REQUIRED_ERROR_MESSAGES.PURCHASE_RESPONSIBLE,
        label: "Кто у вас занимается закупками:",
        options: ["Я", "Другой сотрудник"],
      },
      {
        name: "position",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.POSITION,
        label: "Укажите Вашу должность",
        condition: { field: "purchaseResponsible", value: "Я" },
      },
      { name: "phoneNumber", type: "tel", required: REQUIRED_ERROR_MESSAGES.PHONE, label: "Номер телефона" },
      { name: "fullName", type: "text", required: REQUIRED_ERROR_MESSAGES.FULL_NAME, label: "Ф.И.О." },
      { name: "city", type: "text", required: REQUIRED_ERROR_MESSAGES.CITY, label: "Населенный пункт" },
      {
        name: "deliveryAddress",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.DELIVERY_ADDRESS,
        label: "Адрес доставки",
      },
    ],
  },
  {
    id: "4",
    allowSelectTheSameTime: true,
    choice: "У нас/у меня кондитерская, кофейня, ресторан  и т.д.",
    fields: [
      {
        name: "establishmentType",
        type: "checkbox",
        required: REQUIRED_ERROR_MESSAGES.ESTABLISHMENT_TYPE,
        label: "Какой из вариантов Вам подходит?",
        options: ["Кондитерская", "Ресторан", "Кофейня"],
        hasCustomOption: true,
        customOptionLabel: "Свой вариант",
        hint: "Можно выбрать несколько",
      },
      {
        name: "establishmentName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.ESTABLISHMENT_NAME,
        label: "Укажите название вашего заведения как написано в оригинале",
      },
      {
        name: "multipleEstablishments",
        type: "radio",
        required: REQUIRED_ERROR_MESSAGES.MULTIPLE_ESTABLISHMENTS,
        label: "У Вас одно заведение или несколько?",
        options: ["Одно", "Несколько"],
      },
      {
        name: "establishmentCount",
        type: "number",
        required: REQUIRED_ERROR_MESSAGES.ESTABLISHMENT_COUNT,
        label: "Указать количество",
        condition: { field: "multipleEstablishments", value: "Несколько" },
      },
      {
        name: "establishmentCharacteristics",
        type: "radio",
        required: REQUIRED_ERROR_MESSAGES.ESTABLISHMENT_CHARACTERISTICS,
        label: "Какой из вариантов относится к вам?",
        options: ["Заведение с посадочными местами", "Заведение с витриной для продаж", "Подходит оба варианта"],
      },
      {
        name: "purchaseResponsible",
        type: "radio",
        required: REQUIRED_ERROR_MESSAGES.PURCHASE_RESPONSIBLE,
        label: "Кто у вас занимается закупками:",
        options: ["Я", "Другой сотрудник"],
      },
      {
        name: "position",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.POSITION,
        label: "Укажите Вашу должность",
        condition: { field: "purchaseResponsible", value: "Я" },
      },
      {
        name: "phoneNumber",
        type: "tel",
        required: REQUIRED_ERROR_MESSAGES.PHONE,
        label: "Номер телефона",
      },
      {
        name: "fullName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.FULL_NAME,
        label: "Ф.И.О.",
        hint: "Нам важно знать Ваше имя чтоб внести Вас как нашего клиента (Отчество не обязательно)",
      },
      {
        name: "city",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.CITY,
        label: "Населенный пункт",
      },
      {
        name: "deliveryAddress",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.DELIVERY_ADDRESS,
        label: "Адрес доставки",
      },
    ],
  },
  {
    id: "5",
    allowSelectTheSameTime: false,

    choice: "У нас/у меня супермаркет, продуктовый магазин и т.д",
    fields: [
      {
        name: "storeName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.STORE_NAME,
        label: "Укажите название Вашего магазина/супермаркета (как на вывеске)",
      },
      {
        name: "purchaseResponsible",
        type: "radio",
        required: REQUIRED_ERROR_MESSAGES.PURCHASE_RESPONSIBLE,
        label: "Кто у вас занимается закупками:",
        options: ["Я", "Другой сотрудник"],
      },
      {
        name: "position",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.POSITION,
        label: "Укажите Вашу должность",
        condition: { field: "purchaseResponsible", value: "Я" },
      },
      { name: "phoneNumber", type: "tel", required: REQUIRED_ERROR_MESSAGES.PHONE, label: "Номер телефона" },
      { name: "fullName", type: "text", required: false, label: "Ф.И.О." },
      {
        name: "city",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.CITY,
        label: "Населенный пункт",
        hint: "район, город, село и т.д.",
      },
      {
        name: "deliveryAddress",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.DELIVERY_ADDRESS,
        label: "Адрес доставки",
      },
    ],
  },
  {
    id: "6",
    allowSelectTheSameTime: false,
    choice: "Магазин товаров для кондитеров,ресторанов и т.д.",
    fields: [
      {
        name: "storeName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.STORE_NAME,
        label: "Укажите название Вашего магазина (как на вывеске)",
      },
      {
        name: "purchaseResponsible",
        type: "radio",
        required: REQUIRED_ERROR_MESSAGES.PURCHASE_RESPONSIBLE,
        label: "Кто у вас занимается закупками:",
        options: ["Я", "Другой сотрудник"],
      },
      {
        name: "position",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.POSITION,
        label: "Укажите Вашу должность",
        condition: { field: "purchaseResponsible", value: "Я" },
      },
      { name: "phoneNumber", type: "tel", required: REQUIRED_ERROR_MESSAGES.PHONE, label: "Номер телефона" },
      {
        name: "fullName",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.FULL_NAME,
        label: "Ф.И.О.",
        hint: "Нам важно знать Ваше имя чтоб внести Вас как нашего клиента (Отчество не обязательно)",
      },
      { name: "city", type: "text", required: REQUIRED_ERROR_MESSAGES.CITY, label: "Населенный пункт" },
      {
        name: "deliveryAddress",
        type: "text",
        required: REQUIRED_ERROR_MESSAGES.DELIVERY_ADDRESS,
        label: "Адрес доставки",
      },
    ],
  },
];
